import { LockConfiguration, ParkingStatus } from '~/__generated__/globalTypes'
import {
  ValidityInterval,
  VehicleCategory,
  VehicleRewardType,
  VehicleType,
} from '~/gql/generated/graphql'

export enum SchemeId {
  // Referenced from:
  // https://github.com/team-blaze/basis-utils/blob/master/basis_utils/scheme/scheme_mapping.py
  secret_cycles = '1',
  london = '2',
  bcp = '3',
  barnet = '4',
  hereford = '5',
  raf = '6',
  hackney_council = '7',
  norwich = '8',
  watford = '9',
  staten_island = '10',
  tfl_smartbike = '11',
  isle_of_wight = '12',
  west_midlands = '13',
  london_cargo_bike = '14',
  greater_manchester = '15',
  wool = '16',
  cornwall = '19',
  portsmouth = '20',
  southampton = '21',
  hertsmere = '22',
  plymouth = '23',
  westminster_cargo_bike = '24',
  brighton = '25',
  leeds = '26',
  worcester = '27',
  stevenage = '28',
  guildford = '29',
  weymouth = '30',
  eastleigh = '31',
  bradford = '32',
  belfast = '33',
  canterbury = '34',
}

export const DockedSchemes: SchemeId[] = [
  SchemeId.brighton,
  SchemeId.greater_manchester,
  SchemeId.leeds,
  SchemeId.portsmouth,
  SchemeId.secret_cycles,
  SchemeId.southampton,
  SchemeId.west_midlands,
]

// Should lock configuration be shown?
// This is mostly of intrest if the scheme uses front locks
export const LockConfigurationSchemes: SchemeId[] = [
  SchemeId.brighton,
  SchemeId.greater_manchester,
  SchemeId.guildford,
  SchemeId.leeds,
  SchemeId.secret_cycles,
  SchemeId.stevenage,
]

export const PromotionalZoneSchemes: SchemeId[] = [
  SchemeId.bcp,
  SchemeId.isle_of_wight,
  SchemeId.norwich,
  SchemeId.plymouth,
  SchemeId.worcester,
  SchemeId.guildford,
]

type RewardNames = Record<VehicleRewardType, string>
export const REWARD_TYPES: RewardNames = {
  [VehicleRewardType.rescue_stranded]: 'Stranded vehicle rescue',
  [VehicleRewardType.secure_front_lock]: 'Use front lock',
} as const

export const ELECTRIONIC_LOCKS = [
  LockConfiguration.front_and_electronic_rear_lock,
  LockConfiguration.electronic_lock,
]

export const SCHEME_EMOJIS: Record<SchemeId, string> = {
  [SchemeId.secret_cycles]: '🤫',
  [SchemeId.london]: '🏰',
  [SchemeId.bcp]: '🏖️',
  [SchemeId.barnet]: '💇',
  [SchemeId.hereford]: '🍏',
  [SchemeId.raf]: '🛩️',
  [SchemeId.hackney_council]: '☕️',
  [SchemeId.norwich]: '🚜',
  [SchemeId.watford]: '⚽️',
  [SchemeId.staten_island]: '🗽',
  [SchemeId.tfl_smartbike]: '🚌',
  [SchemeId.isle_of_wight]: '🏝️',
  [SchemeId.west_midlands]: '🍫',
  [SchemeId.london_cargo_bike]: '📦',
  [SchemeId.greater_manchester]: '🐝',
  [SchemeId.wool]: '🐑',
  [SchemeId.cornwall]: '🥟',
  [SchemeId.portsmouth]: '⚓️',
  [SchemeId.southampton]: '🚢',
  [SchemeId.hertsmere]: '🦌',
  [SchemeId.plymouth]: '⛵️',
  [SchemeId.westminster_cargo_bike]: '🏛️',
  [SchemeId.brighton]: '🗼',
  [SchemeId.leeds]: '🦉',
  [SchemeId.worcester]: '🥫',
  [SchemeId.stevenage]: '🏫',
  [SchemeId.guildford]: '💼',
  [SchemeId.weymouth]: '🦕',
  [SchemeId.eastleigh]: '🦢',
  [SchemeId.bradford]: '📽️',
  [SchemeId.belfast]: '💫',
  [SchemeId.canterbury]: '📜',
}

// Old schemes still in DB but hide from Dashboard
export const LEGACY_SCHEMES: SchemeId[] = [
  SchemeId.barnet,
  SchemeId.raf,
  SchemeId.staten_island,
]

export const DEVICE_WARNING_COUNT = 1

export const VEHICLE_TYPES: Record<VehicleCategory, VehicleType> = {
  [VehicleCategory.bike]: VehicleType.bike,
  [VehicleCategory.ebike]: VehicleType.bike,
  [VehicleCategory.escooter]: VehicleType.scooter,
  [VehicleCategory.cargo_ebike]: VehicleType.cargo_bike,
}

export const VEHICLE_CATEGORY_LABELS: Record<VehicleCategory, string> = {
  [VehicleCategory.bike]: 'Bike',
  [VehicleCategory.ebike]: 'e-Bike',
  [VehicleCategory.escooter]: 'e-Scooter',
  [VehicleCategory.cargo_ebike]: 'Cargo bike',
}

export const PARKING_DESCRIPTION: Record<ParkingStatus, string> = {
  [ParkingStatus.valid]: 'In bay',
  [ParkingStatus.out_of_zone]: 'Out of bay',
  [ParkingStatus.out_of_service_area]: 'Out of service area',
  [ParkingStatus.vehicle_not_permitted]: "Bay doesn't allow vehicle",
  [ParkingStatus.zone_max_capacity]: 'Bay at max capacity',
}

// FCM topic the backend publishes to when a rider uploads a parking photo, and
// the matching `payload.data.eventType` value on the incoming notification.
export const PHOTO_UPLOAD_TOPIC = 'photo.upload'

// Browser CustomEvent used to relay an incoming photo.upload notification from
// the (non-React) Firebase messaging handler into the React tree.
export const PHOTO_UPLOAD_EVENT = 'beryl:photo-upload'

export const VALIDITY_INTERVAL_LABELS: Record<ValidityInterval, string> = {
  [ValidityInterval.p1_d]: '1 day',
  [ValidityInterval.p2_d]: '2 days',
  [ValidityInterval.p3_d]: '3 days',
  [ValidityInterval.p7_d]: 'A week',
  [ValidityInterval.p14_d]: '2 weeks',
  [ValidityInterval.p1_m]: 'A month',
  [ValidityInterval.p3_m]: '3 months',
  [ValidityInterval.p6_m]: '6 months',
  [ValidityInterval.p1_y]: 'A year',
}
